/**
 * RoleFilterDropdown - Multi-select filter for card gameplay roles in deck list
 */
import type { ReactElement } from 'react';
import { useCallback, useState } from 'react';

import { useClickAway } from '@uidotdev/usehooks';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import { FaCheck, FaTags, FaTimes } from 'react-icons/fa';

import { CARD_ROLE_INFO, CARD_ROLE_ORDER } from '@/lib/cardClassification';
import type { CardRoleId } from '@/types/card';

import { deckRoleFilterAtom } from '../state/deckEditorSettings';

// ============================================================================
// Types
// ============================================================================

export interface RoleFilterDropdownProps {
  className?: string;
}

// ============================================================================
// Main Component
// ============================================================================

export default function RoleFilterDropdown({ className }: RoleFilterDropdownProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRoles, setSelectedRoles] = useAtom(deckRoleFilterAtom);

  const dropdownRef = useClickAway<HTMLDivElement>(() => setIsOpen(false));

  const toggleRole = useCallback(
    (role: CardRoleId) => {
      setSelectedRoles((prev) => {
        if (prev.includes(role)) {
          return prev.filter((r) => r !== role);
        }
        return [...prev, role];
      });
    },
    [setSelectedRoles],
  );

  const clearFilter = useCallback(() => {
    setSelectedRoles([]);
  }, [setSelectedRoles]);

  const activeCount = selectedRoles.length;

  return (
    <div ref={dropdownRef} className={clsx('relative', className)}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
          activeCount > 0
            ? 'bg-aether/20 text-aether'
            : isOpen
              ? 'bg-zinc-700 text-zinc-100'
              : 'text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200',
        )}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <FaTags className="h-3.5 w-3.5" />
        <span>Roles</span>
        {activeCount > 0 && (
          <span className="bg-aether ml-1 flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-semibold text-zinc-900">
            {activeCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 z-50 mt-1 w-56 rounded-lg border border-zinc-700 bg-zinc-800 py-1 shadow-xl" role="menu">
          <div className="px-3 py-2 text-xs font-medium tracking-wider text-zinc-500 uppercase">Filter by Role</div>

          {/* Role options */}
          {CARD_ROLE_ORDER.map((roleId) => {
            const roleInfo = CARD_ROLE_INFO[roleId];
            const isActive = selectedRoles.includes(roleId);
            return (
              <button
                key={roleId}
                type="button"
                role="menuitemcheckbox"
                aria-checked={isActive}
                onClick={() => toggleRole(roleId)}
                className={clsx('flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors', 'hover:bg-zinc-700/50')}
              >
                <span
                  className={clsx(
                    'flex h-4 w-4 items-center justify-center rounded border transition-colors',
                    isActive ? 'border-aether bg-aether text-zinc-900' : 'border-zinc-600 bg-transparent',
                  )}
                >
                  {isActive && <FaCheck className="h-2.5 w-2.5" />}
                </span>
                <span
                  className={clsx(
                    'inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-xs font-medium',
                    roleInfo.bgColor,
                    roleInfo.color,
                  )}
                >
                  {roleInfo.label}
                </span>
              </button>
            );
          })}

          {/* Clear button */}
          {activeCount > 0 && (
            <>
              <div className="my-1 border-t border-zinc-700" />
              <button
                type="button"
                onClick={clearFilter}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-zinc-500 hover:bg-zinc-700/50 hover:text-zinc-300"
              >
                <FaTimes className="h-3 w-3" />
                <span>Clear Filter</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
